import type { VehicleBodyType, VehicleCondition, VehicleFuel, VehicleTransmission } from '@/lib/sanity/dto';
import { isNumber, type VehicleCardModel, type VehicleFacets } from '@/features/vehicle';
import { VEHICLE_MILEAGE_STEP, VEHICLE_PRICE_STEP } from './vehicleListing.constants';

export type VehicleRange = { min: number; max: number };

/** A slider's „von – bis“. An end left at the edge of the scale (`null`) doesn't restrict. */
export type VehicleRangeSelection = { min: number | null; max: number | null };

export type VehicleFilters = {
    condition: VehicleCondition[];
    make: string[];
    /** Model keys (`modelKey`), so two makes can share a model name. */
    model: string[];
    bodyType: VehicleBodyType[];
    fuel: VehicleFuel[];
    transmission: VehicleTransmission[];
    price: VehicleRangeSelection;
    mileage: VehicleRangeSelection;
    year: VehicleRangeSelection;
};

export type VehicleListFacet = 'condition' | 'make' | 'model' | 'bodyType' | 'fuel' | 'transmission';

export type VehicleRangeFacet = 'price' | 'mileage' | 'year';

/** The sliders in the design's order. */
export const VEHICLE_RANGE_FACETS: VehicleRangeFacet[] = ['price', 'mileage', 'year'];

export const OPEN_RANGE: VehicleRangeSelection = { min: null, max: null };

export const EMPTY_VEHICLE_FILTERS: VehicleFilters = {
    condition: [],
    make: [],
    model: [],
    bodyType: [],
    fuel: [],
    transmission: [],
    price: OPEN_RANGE,
    mileage: OPEN_RANGE,
    year: OPEN_RANGE,
};

/** Slider ends from the stock; `null` where the stock leaves nothing to narrow. */
export type VehicleFilterBounds = {
    price: VehicleRange | null;
    mileage: VehicleRange | null;
    year: VehicleRange | null;
};

export function modelKey(make?: string, model?: string): string {
    return `${make ?? ''}|${model ?? ''}`;
}

/** The ends are rounded outwards to the step, so the whole scale spans every vehicle. */
function rangeOf(values: (number | undefined)[], step: number): VehicleRange | null {
    const numbers = values.filter(isNumber);
    if (!numbers.length) return null;

    const lowest = Math.min(...numbers);
    const highest = Math.max(...numbers);
    if (highest <= lowest) return null;

    return { min: Math.floor(lowest / step) * step, max: Math.ceil(highest / step) * step };
}

export function filterBounds(vehicles: VehicleCardModel[]): VehicleFilterBounds {
    return {
        price: rangeOf(
            vehicles.map((vehicle) => vehicle.facets.price),
            VEHICLE_PRICE_STEP,
        ),
        mileage: rangeOf(
            vehicles.map((vehicle) => vehicle.facets.mileage),
            VEHICLE_MILEAGE_STEP,
        ),
        year: rangeOf(
            vehicles.map((vehicle) => vehicle.facets.registrationYear),
            1,
        ),
    };
}

function selects<T>(selected: T[], value: T | undefined): boolean {
    return !selected.length || (value !== undefined && selected.includes(value));
}

/** Both thumbs as numbers: an open end sits at the edge of the scale. */
export function thumbsOf(selection: VehicleRangeSelection, bounds: VehicleRange): VehicleRange {
    return { min: selection.min ?? bounds.min, max: selection.max ?? bounds.max };
}

/** Back from the thumbs: one at the edge of the scale lifts its restriction. */
export function selectionOf(thumbs: VehicleRange, bounds: VehicleRange): VehicleRangeSelection {
    return {
        min: thumbs.min > bounds.min ? thumbs.min : null,
        max: thumbs.max < bounds.max ? thumbs.max : null,
    };
}

export function isRestricted(selection: VehicleRangeSelection): boolean {
    return selection.min !== null || selection.max !== null;
}

/** A vehicle without the value (no price, never registered) is never filtered out by a slider. */
function withinRange(value: number | undefined, selection: VehicleRangeSelection): boolean {
    if (!isNumber(value)) return true;

    return (selection.min === null || value >= selection.min) && (selection.max === null || value <= selection.max);
}

function withinRanges(facets: VehicleFacets, filters: VehicleFilters): boolean {
    return (
        withinRange(facets.price, filters.price) &&
        withinRange(facets.mileage, filters.mileage) &&
        withinRange(facets.registrationYear, filters.year)
    );
}

/** OR within a facet, AND across facets. `skip` leaves one facet out, for its live counts. */
export function matchesFilters(vehicle: VehicleCardModel, filters: VehicleFilters, skip?: VehicleListFacet): boolean {
    const { facets } = vehicle;

    const checks: Record<VehicleListFacet, boolean> = {
        condition: selects(filters.condition, facets.condition),
        make: selects(filters.make, facets.make),
        model: selects(filters.model, modelKey(facets.make, facets.model)),
        bodyType: selects(filters.bodyType, facets.bodyType),
        fuel: selects(filters.fuel, facets.fuel),
        transmission: selects(filters.transmission, facets.transmission),
    };

    const passesLists = (Object.keys(checks) as VehicleListFacet[]).every((facet) => facet === skip || checks[facet]);

    return passesLists && withinRanges(facets, filters);
}

export function toggleValue<T>(values: T[], value: T): T[] {
    return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

/** Deselecting a make takes its models along — the combination could never match. */
export function toggleMake(filters: VehicleFilters, make: string): VehicleFilters {
    const nextMakes = toggleValue(filters.make, make);
    const keepsModel = (key: string) => !nextMakes.length || nextMakes.some((item) => key.startsWith(`${item}|`));

    return {
        ...filters,
        make: nextMakes,
        model: filters.model.filter(keepsModel),
    };
}

export function hasActiveFilters(filters: VehicleFilters): boolean {
    const lists: VehicleListFacet[] = ['condition', 'make', 'model', 'bodyType', 'fuel', 'transmission'];

    return (
        lists.some((facet) => filters[facet].length > 0) ||
        VEHICLE_RANGE_FACETS.some((facet) => isRestricted(filters[facet]))
    );
}
